import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const PREVIEW_STORAGE_PREFIX = 'dragonfullav:public-site-preview';

const getStorageKey = (): string => {
  if (typeof window === 'undefined') {
    return PREVIEW_STORAGE_PREFIX;
  }
  return `${PREVIEW_STORAGE_PREFIX}:${window.location.host}`;
};

const readPreviewState = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.sessionStorage.getItem(getStorageKey()) === 'true';
};

interface PublicSitePreviewContextValue {
  previewActive: boolean;
  enterPreview: () => void;
  exitPreview: () => void;
}

const PublicSitePreviewContext = createContext<PublicSitePreviewContextValue>({
  previewActive: false,
  enterPreview: () => undefined,
  exitPreview: () => undefined,
});

export const PublicSitePreviewProvider = ({ children }: { children: ReactNode }) => {
  const [previewActive, setPreviewActive] = useState(readPreviewState);

  const enterPreview = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(getStorageKey(), 'true');
    }
    setPreviewActive(true);
  }, []);

  const exitPreview = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(getStorageKey());
    }
    setPreviewActive(false);
  }, []);

  const value = useMemo(
    () => ({ previewActive, enterPreview, exitPreview }),
    [enterPreview, exitPreview, previewActive],
  );

  return (
    <PublicSitePreviewContext.Provider value={value}>
      {children}
    </PublicSitePreviewContext.Provider>
  );
};

export const usePublicSitePreview = (): PublicSitePreviewContextValue => (
  useContext(PublicSitePreviewContext)
);

export const getPublicSitePreviewStorageKey = getStorageKey;
